import * as bootstrap from 'bootstrap';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';

const initialFormState = {
    supportType: 'general',
    subject: '',
    text: '',
    isSubmitting: false,
    errorMessage: '',
};

const emptyTickets = {
    rows: [],
    total: 0,
    page: 0,
    limit: 10,
};

const initialReplyState = {
    message: '',
    isSubmitting: false,
    errorMessage: '',
    successMessage: '',
};

function formatDate(value) {
    if (!value) {
        return 'n/a';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'n/a';
    }

    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function titleCase(value) {
    if (!value) {
        return 'Unset';
    }

    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase());
}

function statusClass(value) {
    const normalized = String(value || '').toLowerCase();

    if (normalized === 'open') {
        return 'admin-badge warning';
    }

    if (normalized === 'resolved') {
        return 'admin-badge success';
    }

    if (normalized === 'closed') {
        return 'admin-badge danger';
    }

    return 'admin-badge neutral';
}

async function readApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
        const resData = await response.json();

        if (!response.ok) {
            throw new Error(resData.message || resData.error || fallbackMessage);
        }

        return resData;
    }

    const text = await response.text();

    if (!response.ok) {
        if (response.status === 404 && text.includes('/api/support/tickets') && text.includes('/reply')) {
            throw new Error('Support replies are not available on the running server yet. Restart the server and try again.');
        }

        throw new Error(fallbackMessage);
    }

    return {};
}

function responseAuthor(response) {
    if (response.responder_type === 'admin') {
        return 'Pagado support';
    }

    if (response.responder_type === 'user') {
        return 'You';
    }

    return titleCase(response.responder_type);
}

function SupportConversation({ ticket, replyState, onReplyChange, onReplySubmit }) {
    const responses = ticket?.responses || [];
    const entries = [
        {
            id: 'ticket-message',
            responder_type: 'user',
            message: ticket.description,
            created_at: ticket.created_at,
            isOriginal: true,
        },
        ...responses,
    ];

    return (
        <div className="support-conversation">
            <div className="support-thread-list">
                {entries.map(entry => (
                    <div className={`support-conversation-entry ${entry.responder_type}`} key={entry.id}>
                        <div className="support-conversation-meta">
                            <strong>{entry.isOriginal ? 'You opened this ticket' : responseAuthor(entry)}</strong>
                            <span>{formatDate(entry.created_at)}</span>
                        </div>
                        <p>{entry.message}</p>
                    </div>
                ))}
            </div>

            {responses.length === 0 && (
                <div className="support-conversation-empty">No support replies yet.</div>
            )}

            <form className="support-reply-form" onSubmit={onReplySubmit}>
                <label className="label" htmlFor={`support-reply-${ticket.id}`}>Reply</label>
                <textarea
                    id={`support-reply-${ticket.id}`}
                    className="form-control"
                    value={replyState.message}
                    onChange={event => onReplyChange(event.target.value)}
                    rows="3"
                    placeholder="Write a reply"
                    disabled={replyState.isSubmitting}
                />
                {replyState.errorMessage && <div className="support-reply-error">{replyState.errorMessage}</div>}
                {replyState.successMessage && <div className="support-reply-success">{replyState.successMessage}</div>}
                <div className="support-reply-actions">
                    <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={replyState.isSubmitting || !replyState.message.trim()}
                    >
                        {replyState.isSubmitting ? 'Sending...' : 'Send reply'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Support() {
    const { state } = useContext(AuthContext);
    const [data, setData] = useState(initialFormState);
    const [tickets, setTickets] = useState(emptyTickets);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [ticketsError, setTicketsError] = useState('');
    const [selectedTicket, setSelectedTicket] = useState({
        id: null,
        loading: false,
        error: '',
        data: null,
    });
    const [replyState, setReplyState] = useState(initialReplyState);
    const [operation, setOperation] = useState({
        success: false,
        message: '',
    });

    const loadTickets = useCallback(async () => {
        if (!state.isAuthenticated) {
            return;
        }

        setTicketsLoading(true);
        setTicketsError('');

        try {
            const response = await fetch('/api/support/tickets?limit=10');
            const resData = await readApiResponse(response, 'Could not load support tickets');

            setTickets(resData.content || emptyTickets);
        } catch (err) {
            setTicketsError(err.message || 'Could not load support tickets');
        } finally {
            setTicketsLoading(false);
        }
    }, [state.isAuthenticated]);

    useEffect(() => {
        let appendedScript = null;

        if (!document.getElementById('recaptcha-script')) {
            appendedScript = document.createElement('script');
            appendedScript.src = 'https://www.google.com/recaptcha/api.js';
            appendedScript.async = true;
            appendedScript.defer = true;
            appendedScript.id = 'recaptcha-script';
            document.body.appendChild(appendedScript);
        }

        return () => {
            if (appendedScript?.parentNode) {
                appendedScript.parentNode.removeChild(appendedScript);
            }
        };
    }, []);

    useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    const handleInputChange = event => {
        setData(current => ({
            ...current,
            [event.target.name]: event.target.value,
        }));
    };

    const showModal = id => {
        const element = document.getElementById(id);

        if (!element) {
            return;
        }

        const modal = new bootstrap.Modal(element, {});
        modal.show();
    };


    const toggleTicket = async ticket => {
        setReplyState(initialReplyState);

        if (selectedTicket.id === ticket.id) {
            setSelectedTicket({
                id: null,
                loading: false,
                error: '',
                data: null,
            });
            return;
        }

        setSelectedTicket({
            id: ticket.id,
            loading: true,
            error: '',
            data: null,
        });

        try {
            const response = await fetch(`/api/support/tickets/${ticket.id}`);
            const resData = await readApiResponse(response, 'Could not load support conversation');

            setSelectedTicket({
                id: ticket.id,
                loading: false,
                error: '',
                data: resData.content,
            });
        } catch (err) {
            setSelectedTicket({
                id: ticket.id,
                loading: false,
                error: err.message || 'Could not load support conversation',
                data: null,
            });
        }
    };

    const handleReplyChange = message => {
        setReplyState(current => ({
            ...current,
            message,
            errorMessage: '',
            successMessage: '',
        }));
    };

    const handleReplySubmit = async event => {
        event.preventDefault();

        const ticketId = selectedTicket.id;
        const message = replyState.message.trim();

        if (!ticketId || !message) {
            return;
        }

        setReplyState(current => ({
            ...current,
            isSubmitting: true,
            errorMessage: '',
            successMessage: '',
        }));

        try {
            const response = await fetch(`/api/support/tickets/${ticketId}/reply`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message }),
            });
            const resData = await readApiResponse(response, 'Could not send support reply');

            setSelectedTicket({
                id: ticketId,
                loading: false,
                error: '',
                data: resData.content,
            });
            setReplyState({
                ...initialReplyState,
                successMessage: 'Reply sent.',
            });
            await loadTickets();
        } catch (err) {
            setReplyState(current => ({
                ...current,
                isSubmitting: false,
                errorMessage: err.message || 'Could not send support reply',
            }));
        }
    };

    const handleFormSubmit = async event => {
        event.preventDefault();

        setData(current => ({
            ...current,
            isSubmitting: true,
            errorMessage: '',
        }));

        const recaptchaResponse = window.grecaptcha?.getResponse();

        if (!recaptchaResponse) {
            setData(current => ({
                ...current,
                isSubmitting: false,
                errorMessage: 'Verify reCAPTCHA',
            }));
            return;
        }

        const recapResponse = await fetch('/api/auth/recaptcha-verify', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                response: recaptchaResponse,
            }),
        });

        const recapVerifyResp = await readApiResponse(recapResponse, 'reCAPTCHA verification failed');

        if (!recapVerifyResp.success) {
            setData(current => ({
                ...current,
                isSubmitting: false,
                errorMessage: 'reCAPTCHA verification failed',
            }));
            return;
        }

        try {
            const response = await fetch('/api/support/send-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    supportType: data.supportType,
                    subject: data.subject,
                    text: data.text,
                }),
            });

            const resData = await readApiResponse(response, 'Could not create support ticket');

            const ticketId = resData.content?.ticket?.id;
            setOperation({
                success: true,
                message: ticketId ? `Support ticket #${ticketId} created.` : resData.message,
            });
            setData(initialFormState);
            window.grecaptcha?.reset();
            await loadTickets();
            showModal('actionSheetAlertSuccess');
        } catch (err) {
            setOperation({
                success: false,
                message: err.message || 'Could not create support ticket',
            });
            showModal('actionSheetAlertError');
        } finally {
            setData(current => ({
                ...current,
                isSubmitting: false,
            }));
        }
    };

    if (state.loading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            {!state.isAuthenticated && <Navigate replace to="/login" />}
            <Navigation />
            <div id="appCapsule">
                <div className="section mt-4 text-center">
                    <h1>Support</h1>
                </div>
                <div className="section mb-5 p-2">
                    <form onSubmit={handleFormSubmit}>
                        <div className="card">
                            <div className="card-body pb-1">
                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" htmlFor="supportType">Request type</label>
                                        <select
                                            className="form-control custom-select"
                                            id="supportType"
                                            name="supportType"
                                            value={data.supportType}
                                            onChange={handleInputChange}
                                        >
                                            <option value="general">General</option>
                                            <option value="technical">Technical</option>
                                            <option value="billing">Billing</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" htmlFor="subject">Subject</label>
                                        <input
                                            type="text"
                                            name="subject"
                                            className="form-control"
                                            placeholder="Enter the subject"
                                            id="subject"
                                            autoComplete="off"
                                            value={data.subject}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" htmlFor="text">Details</label>
                                        <textarea
                                            className="form-control"
                                            name="text"
                                            placeholder="Enter description"
                                            id="text"
                                            autoComplete="off"
                                            value={data.text}
                                            rows="4"
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="g-recaptcha mt-2 mb-3" data-sitekey={process.env.REACT_APP_RECAPTCHA_KEY}></div>
                        </div>

                        {data.errorMessage && <div className="mb-3 text-danger">{data.errorMessage}</div>}

                        <div className="form-group transparent mt-5">
                            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={data.isSubmitting}>
                                {data.isSubmitting ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </form>

                    <div className="mt-4">
                        <div className="section-title">Your support tickets</div>
                        {ticketsError && <div className="alert alert-warning">{ticketsError}</div>}
                        {ticketsLoading && <div className="card"><div className="card-body">Loading tickets...</div></div>}
                        {!ticketsLoading && tickets.rows.length === 0 && (
                            <div className="card"><div className="card-body">No support tickets yet.</div></div>
                        )}
                        {!ticketsLoading && tickets.rows.map(ticket => {
                            const replyCount = Number(ticket.response_count || 0);
                            const isSelected = selectedTicket.id === ticket.id;

                            return (
                                <div className="card mb-2 support-ticket-card" key={ticket.id}>
                                    <div className="card-body">
                                        <div className="support-ticket-header">
                                            <div className="support-ticket-main">
                                                <strong>#{ticket.id} {ticket.subject}</strong>
                                                <div className="support-ticket-meta">
                                                    {titleCase(ticket.support_type)} · Created {formatDate(ticket.created_at)}
                                                    {ticket.latest_response_at ? ` · Last reply ${formatDate(ticket.latest_response_at)}` : ''}
                                                </div>
                                            </div>
                                            <div className="support-ticket-actions">
                                                <button
                                                    type="button"
                                                    className={`support-reply-pill ${replyCount > 0 ? 'has-replies' : ''}`}
                                                    onClick={() => toggleTicket(ticket)}
                                                    aria-expanded={isSelected}
                                                >
                                                    {isSelected ? 'Hide thread' : `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
                                                </button>
                                                <span className={statusClass(ticket.status)}>{titleCase(ticket.status)}</span>
                                            </div>
                                        </div>
                                        <p className="support-ticket-message">{ticket.description}</p>

                                        {isSelected && selectedTicket.loading && (
                                            <div className="support-conversation-empty mt-3">Loading conversation...</div>
                                        )}
                                        {isSelected && selectedTicket.error && (
                                            <div className="alert alert-warning mt-3">{selectedTicket.error}</div>
                                        )}
                                        {isSelected && selectedTicket.data && (
                                            <SupportConversation
                                                ticket={selectedTicket.data}
                                                replyState={replyState}
                                                onReplyChange={handleReplyChange}
                                                onReplySubmit={handleReplySubmit}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="modal fade action-sheet" id="actionSheetAlertSuccess" data-bs-backdrop="static" tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className="action-sheet-content">
                                <div className="iconbox text-success">
                                    <ion-icon name="checkmark-circle" role="img" class="md hydrated" aria-label="checkmark circle"></ion-icon>
                                </div>
                                <div className="text-center p-2">
                                    <h3>Success</h3>
                                    <p>{operation.message}</p>
                                </div>
                                <button type="button" className="btn btn-primary btn-lg btn-block" data-bs-dismiss="modal">Ok</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade action-sheet" id="actionSheetAlertError" data-bs-backdrop="static" tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className="action-sheet-content">
                                <div className="iconbox text-danger">
                                    <ion-icon name="alert-circle" role="img" class="md hydrated" aria-label="alert circle"></ion-icon>
                                </div>
                                <div className="text-center p-2">
                                    <h3>Error</h3>
                                    <p>{operation.message}</p>
                                </div>
                                <button type="button" className="btn btn-primary btn-lg btn-block" data-bs-dismiss="modal">Try again</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}

export default Support;
